import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { DailyLogReaction, DailyLogReactionType, DailyLogSelectedReaction } from 'src/app/interfaces/daily-log'
import { LogService } from 'src/app/services/log/log.service'
import { ModalService } from 'src/app/services/modal/modal.service'
import { UserService } from 'src/app/services/user/user.service'
import { DailyLogReactionDetailModalComponent } from './daily-log-reaction-detail-modal/daily-log-reaction-detail-modal.component'
import { DailyLogReactionPrivateModalComponent } from './daily-log-reaction-private-modal/daily-log-reaction-private-modal.component'

@Component({
  selector: 'app-daily-log-reaction',
  templateUrl: './daily-log-reaction.component.html',
  styleUrls: ['./daily-log-reaction.component.scss'],
})
export class DailyLogReactionComponent implements OnInit, OnChanges {
  constructor(
    private userService: UserService,
    private elementRef: ElementRef,
    private modalService: ModalService,
    private logService: LogService
  ) {}

  @Input() isHost: boolean | undefined
  @Input() reaction: DailyLogReaction | undefined
  @Input() creatingReaction: DailyLogSelectedReaction | undefined
  @Input() isFakeReaction = false
  @Input() isFocused = false
  @Input() limitTextLines = true
  @Input() ignoreGestureStyle = false
  // 스티커 클릭시 자세히 보기 가능 여부
  @Input() isDetailViewable = false
  @Input() showNameMark = false
  @Output() onClickItem = new EventEmitter<number>()
  @Output() onModalClosed = new EventEmitter<void>()
  @Output() deleteCreatingReactionClicked = new EventEmitter<void>()
  @Output() onChangeCreatingReaction = new EventEmitter<DailyLogSelectedReaction>()

  eDailyLogReactionType = DailyLogReactionType

  containerStyle: { [key: string]: string | number } = {}
  reactionType: DailyLogReactionType | undefined
  memo: string | undefined
  profileImageUrl: string | undefined
  commentCount: number | undefined
  imageUrl: string | undefined
  anonymousNumber: string | undefined
  isTemplateSticker: boolean | undefined
  name: string | undefined
  contentWidth: number | undefined
  contentHeightDiff: number = 0
  nameStyle: { [key: string]: string | number } | undefined
  width: number | undefined
  height: number | undefined
  isRoundBordered: boolean = false

  private x: number = 0
  private y: number = 0
  private scale: number = 1
  private angle: number = 0

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('reaction') && changes.reaction.currentValue) {
      const reaction: DailyLogReaction = changes.reaction.currentValue
      this.reactionType = reaction.type
      this.memo = reaction.memo
      this.profileImageUrl = reaction.profileImageUrl
      this.commentCount = reaction.commentCount
      this.imageUrl = reaction.imageUrl
      this.anonymousNumber = reaction.anonymousNumber
      this.name = reaction.name ?? ''
      if (reaction.type !== DailyLogReactionType.MEMO) {
        this.width = reaction.width
        this.height = reaction.height
        this.isRoundBordered = reaction.isRoundBordered ?? false
      }
      if (!this.ignoreGestureStyle) {
        this.containerStyle = {
          position: 'absolute',
          top: `${reaction.location.heightRatio}%`,
          left: `${reaction.location.widthRatio}%`,
          transform: `scale(${reaction.scale}) rotate(${reaction.rotate}rad)`,
        }
        if (this.name) {
          const calcWidthTimeout = setTimeout(() => {
            const reactionElem = this.elementRef.nativeElement.querySelector('.reaction-wrapper')
            if (reactionElem) {
              const reactionRect = reactionElem.getBoundingClientRect()
              const contentWidth = reactionRect.width
              const contentWidthDiff = reactionRect.width - reactionElem.offsetWidth
              const contentHeightDiff = reactionRect.height - reactionElem.offsetHeight
              this.nameStyle = {
                top: this.containerStyle.top,
                left: this.containerStyle.left,
                width: `${contentWidth}px`,
                transform: `rotate(${reaction.rotate}rad) translate(${-contentWidthDiff / 2}px, calc(-120% - ${
                  contentHeightDiff / 2
                }px))`,
                'z-index': 999,
              }
            }
            clearTimeout(calcWidthTimeout)
          }, 100)
        }
      }
    }
    if (changes.hasOwnProperty('creatingReaction') && changes.creatingReaction.currentValue) {
      const reaction: DailyLogSelectedReaction = changes.creatingReaction.currentValue
      this.reactionType = reaction.type
      this.memo = reaction.type === DailyLogReactionType.MEMO ? reaction.content : undefined
      this.profileImageUrl = this.userService.isLoggedIn ? this.userService.user.profile.image : undefined
      this.commentCount = 0
      this.imageUrl = reaction.type !== DailyLogReactionType.MEMO ? reaction.content : undefined
      this.containerStyle = {
        position: 'relative',
      }
      this.isTemplateSticker = reaction.isTemplateSticker
      if (reaction.type !== DailyLogReactionType.MEMO) {
        this.width = reaction.width
        this.height = reaction.height
        this.isRoundBordered = reaction.isRoundBordered ?? false
      }
      this.x = reaction.x ?? 0
      this.y = reaction.y ?? 0
      this.scale = reaction.scale ?? 1
      this.angle = reaction.angle ?? 0
      this.applyTransform()
    }

    if (changes.hasOwnProperty('isFocused') && changes.isFocused.currentValue === true) {
      this.applyTransform()
    }
  }

  async onClickReaction(): Promise<void> {
    if (this.isDetailViewable && this.reaction) {
      if (this.reaction.isPrivate && !this.isHost && !this.reaction.isMine) {
        const privateModal = await this.modalService.openBottomSheet(DailyLogReactionPrivateModalComponent)
        await privateModal.present()
        return
      }
      this.onClickItem.emit(this.reaction.id)
      this.logService.dailyLog.logDailyLogDetailReactionClicked()
      const detailModal = await this.modalService.openBottomSheet(DailyLogReactionDetailModalComponent, {
        reaction: this.reaction,
        isHost: this.isHost ?? false,
      })
      await detailModal.present()
      await detailModal.onWillDismiss()
      this.onModalClosed.emit()
    }
  }

  onClickDeleteCreatingReaction(): void {
    this.deleteCreatingReactionClicked.emit()
  }

  getBoundingClientRect(): DOMRect | undefined {
    const reactionElem = this.elementRef.nativeElement.querySelector('.reaction-wrapper')
    if (reactionElem) {
      return reactionElem.getBoundingClientRect()
    }
  }

  dragMoveListener(dx: number, dy: number): void {
    if (!this.isFocused) return
    this.x += dx
    this.y += dy
    this.applyTransform()
  }

  gestureMoveListener(currentSize: { width: number; height: number }, ds: number, da: number): void {
    if (!this.isFocused) return
    // Update scale and rotation angle
    const MAX_SIZE = 100
    const newScale = this.scale * (1 + ds)

    const { width, height } = currentSize

    if (ds < 0 || (width * newScale <= Math.max(width, MAX_SIZE) && height * newScale <= Math.max(height, MAX_SIZE))) {
      this.scale = newScale
    }
    this.angle += da
    this.applyTransform()
  }

  dragEndListener(): void {
    this.emitChangedReaction()
  }

  gestureEndListener(): void {
    this.emitChangedReaction()
  }

  getCurrentState(): { x: number; y: number; scale: number; angle: number } {
    return {
      x: this.x,
      y: this.y,
      scale: this.scale,
      angle: this.angle,
    }
  }

  private applyTransform(): void {
    const target = this.elementRef.nativeElement.querySelector('.reaction-wrapper')
    const focusUIElem = this.elementRef.nativeElement.querySelector('.focused-ui')
    /** scale up 하면서 boundingRect가 변하면서 위치가 이상해지는 문제 해결을 위해 fake element를 사용. fake element는 scale을 적용하지 않음 */
    const translateTransform = `translate(${this.x}px, ${this.y}px)`
    const rotateTransform = `rotate(${this.angle}deg)`
    const scaleTransform = `scale(${this.scale})`
    if (this.isFakeReaction) {
      target.style.transform = translateTransform
    } else {
      target.style.transform = `${translateTransform} ${rotateTransform} ${scaleTransform}`
    }

    if (focusUIElem) {
      focusUIElem.style.transform = `${translateTransform} ${rotateTransform} ${scaleTransform}`
      const deleteButtonElem = this.elementRef.nativeElement.querySelector('.focused-ui .delete-button')
      deleteButtonElem.style.transform = `scale(${1 / this.scale})`
    }
  }

  private emitChangedReaction(): void {
    if (this.creatingReaction) {
      this.onChangeCreatingReaction.emit({
        ...this.creatingReaction,
        ...this.getCurrentState(),
      })
    }
  }
}
